import * as C from './constants';
import { AircraftState } from './AircraftState';
import { Engine } from './Engine';
import { sampleISA, densityRatio, Wind, CALM_WIND } from './Atmosphere';
import { ControlSurfaces } from '../systems/Controls';
import { TerrainProvider, FlatTerrain } from '../world/Terrain';

/**
 * One landing gear wheel's body-frame position and per-wheel properties.
 * x forward, y right, z down, all relative to the CG.
 */
interface WheelDef {
  x: number;
  y: number;
  z: number;
  stiffness: number;
  damping: number;
  braked: boolean; // C172 toe brakes act on the mains only; nose is unbraked
  steering: boolean; // nosewheel steering
  side: 'nose' | 'left' | 'right'; // which brakeLeft/brakeRight input applies (nose: neither)
}

const WHEELS: WheelDef[] = [
  {
    x: C.NOSE_GEAR_X, y: C.NOSE_GEAR_Y, z: C.NOSE_GEAR_Z,
    stiffness: C.NOSE_GEAR_STIFFNESS, damping: C.NOSE_GEAR_DAMPING,
    braked: false, steering: true, side: 'nose',
  },
  {
    x: C.MAIN_GEAR_X, y: -C.MAIN_GEAR_Y, z: C.MAIN_GEAR_Z,
    stiffness: C.MAIN_GEAR_STIFFNESS, damping: C.MAIN_GEAR_DAMPING,
    braked: true, steering: false, side: 'left',
  },
  {
    x: C.MAIN_GEAR_X, y: C.MAIN_GEAR_Y, z: C.MAIN_GEAR_Z,
    stiffness: C.MAIN_GEAR_STIFFNESS, damping: C.MAIN_GEAR_DAMPING,
    braked: true, steering: false, side: 'right',
  },
];

interface WheelForce {
  Fx: number; Fy: number; Fz: number; // body-frame force contribution
  Lm: number; Mm: number; Nm: number; // body-frame moment contribution (r x F)
  touching: boolean;
}

const NO_FORCE: WheelForce = { Fx: 0, Fy: 0, Fz: 0, Lm: 0, Mm: 0, Nm: 0, touching: false };

/**
 * Independent spring/damper contact force for one wheel, at its own body-frame
 * position — not a single point at the CG. This is what makes ground behavior
 * (a wing-low touchdown righting itself, differential braking yawing the
 * aircraft, the nosewheel lifting off during rotation, directional damping
 * from tire scrub) emerge from real geometry instead of artificial "keep it
 * level" springs.
 */
function wheelGroundForce(
  wheel: WheelDef,
  R: number[][],
  velN: number, velE: number, velD: number,
  p: number, q: number, r: number,
  alt: number,
  groundH: number,
  rudderCmd: number,
  brakeInput: number,
  dt: number,
): WheelForce {
  // This wheel's vertical position offset, body -> world (world[k] = sum_i
  // R[k][i]*body[i]) — only the D (down) component is needed to know its
  // height above the ground.
  const offD = R[2][0] * wheel.x + R[2][1] * wheel.y + R[2][2] * wheel.z;

  // Wheel MSL altitude vs. the ground surface MSL height at its position
  // (from the terrain provider — a flat plane today). Contact when the wheel
  // is below the surface. With FlatTerrain(0), groundH=0 reproduces the
  // original "ground at MSL 0" behaviour exactly.
  const wheelAlt = alt - offD;
  const compression = Math.max(0, groundH - wheelAlt);
  if (compression <= 0) return NO_FORCE;

  // Velocity of this point on the rigid body = v_CG + omega x r, computed in
  // body frame then rotated to world to get the closing (vertical) and
  // ground-track (horizontal) components this specific wheel actually sees.
  const vCGx = R[0][0] * velN + R[1][0] * velE + R[2][0] * velD;
  const vCGy = R[0][1] * velN + R[1][1] * velE + R[2][1] * velD;
  const vCGz = R[0][2] * velN + R[1][2] * velE + R[2][2] * velD;

  const wx = q * wheel.z - r * wheel.y;
  const wy = r * wheel.x - p * wheel.z;
  const wz = p * wheel.y - q * wheel.x;

  const vPx = vCGx + wx, vPy = vCGy + wy, vPz = vCGz + wz;

  const vPn = R[0][0] * vPx + R[0][1] * vPy + R[0][2] * vPz;
  const vPe = R[1][0] * vPx + R[1][1] * vPy + R[1][2] * vPz;
  const vPd = R[2][0] * vPx + R[2][1] * vPy + R[2][2] * vPz;

  // vPd (this wheel's own world-down velocity) equals d(compression)/dt
  // exactly: compression = -wheelAlt, and wheelAlt's rate of change is -vPd,
  // so d(compression)/dt = +vPd. A damper must ADD resistance as the closing
  // (sinking) rate increases — using "-damping*vPd" here (as the single-point
  // model this replaced also did) is backwards: it REDUCES resistance right
  // when impact velocity is highest, i.e. negative damping, which pumps
  // energy into the system instead of dissipating it. Confirmed by testing:
  // a simple drop test diverged into a runaway bounce until this was fixed.
  const normal = Math.max(0, wheel.stiffness * compression + wheel.damping * vPd);

  // Vertical spring/damper force (world up = -D), converted to body frame.
  let Fx = R[2][0] * -normal;
  let Fy = R[2][1] * -normal;
  let Fz = R[2][2] * -normal;

  // Rolling/braking friction opposing THIS wheel's own ground-track velocity
  // (not just the CG's) — this is what gives real directional (yaw) damping
  // from tire scrub, and lets differential braking actually yaw the aircraft.
  const groundSpeed = Math.hypot(vPn, vPe);
  if (groundSpeed > 1e-4) {
    const mu = C.ROLLING_FRICTION + (wheel.braked ? brakeInput * C.BRAKE_FRICTION : 0);
    const fricMax = mu * normal;
    // Cap the friction force so it can never reverse THIS wheel's own
    // ground-track velocity within a single integration step. Plain Coulomb
    // friction (always fricMax, direction-only depending on velocity) is
    // fine at real taxi speeds, but right as the aircraft settles to a near
    // stop — including just sitting still with wind blowing it into a slow
    // creep — it overshoots past zero speed and reverses every step. Since
    // each wheel reacts to its own (rotation-coupled) velocity, that
    // per-step overshoot doesn't cancel symmetrically between the left and
    // right main wheels; it feeds a roll oscillation that full brake
    // (raising mu, and so the overshoot magnitude) made large enough to
    // visibly flip the aircraft. Capping the impulse at "just enough to
    // stop this step" removes the overshoot entirely while leaving normal
    // rolling/braking deceleration at real speed completely unchanged
    // (mass*groundSpeed/dt is always far larger than fricMax there).
    const stopImpulse = dt > 0 ? (C.MASS * groundSpeed) / dt : fricMax;
    const fric = Math.min(fricMax, stopImpulse);
    const fn = (-vPn / groundSpeed) * fric;
    const fe = (-vPe / groundSpeed) * fric;
    Fx += R[0][0] * fn + R[1][0] * fe;
    Fy += R[0][1] * fn + R[1][1] * fe;
    Fz += R[0][2] * fn + R[1][2] * fe;
  }

  // Nosewheel steering: a real castering nosewheel gives ground-steering
  // authority proportional to rudder-pedal deflection and how much weight is
  // on it — which naturally tapers off as the nose lifts during rotation.
  if (wheel.steering) {
    Fy += rudderCmd * C.NOSEWHEEL_STEER_COEFF * normal;
  }

  // Moment about the CG from this wheel's force at its offset: M = r x F.
  const Lm = wheel.y * Fz - wheel.z * Fy;
  const Mm = wheel.z * Fx - wheel.x * Fz;
  const Nm = wheel.x * Fy - wheel.y * Fx;

  return { Fx, Fy, Fz, Lm, Mm, Nm, touching: true };
}

/**
 * Six-degree-of-freedom rigid-body flight model for the C172S.
 *
 * Forces and moments are built in the body frame from a standard aerodynamic
 * coefficient set (parabolic drag polar, linear-then-stalling lift curve,
 * damping + control derivatives), integrated with a fixed timestep. Euler
 * angles are propagated from body rates. Ground contact is a real 3-point
 * tricycle gear model (see wheelGroundForce/WHEELS above) — nose + left/right
 * main wheels, each with its own independent contact force — so takeoff,
 * landing, and taxi behavior emerges from the actual gear geometry.
 */
export class FlightModel {
  engine = new Engine();
  wind: Wind = CALM_WIND;
  // Ground surface model. Defaults to a flat plane at MSL 0 (the original
  // behaviour); main.ts sets it to the active airport's terrain. The single
  // seam where a future elevation model plugs in.
  terrain: TerrainProvider = new FlatTerrain(0);

  step(state: AircraftState, controls: ControlSurfaces, dt: number): void {
    const alt = -state.pos.d;
    const atmo = sampleISA(alt);
    const rho = atmo.density;

    // --- Orientation trig & body<->world direction cosine matrix ----------
    const cphi = Math.cos(state.phi), sphi = Math.sin(state.phi);
    const cth = Math.cos(state.theta), sth = Math.sin(state.theta);
    const cpsi = Math.cos(state.psi), spsi = Math.sin(state.psi);

    // R: body -> world (NED). Rows are world axes, cols are body axes.
    const R = [
      [cth * cpsi, sphi * sth * cpsi - cphi * spsi, cphi * sth * cpsi + sphi * spsi],
      [cth * spsi, sphi * sth * spsi + cphi * cpsi, cphi * sth * spsi - sphi * cpsi],
      [-sth, sphi * cth, cphi * cth],
    ];

    // --- Airspeed vector in body frame (relative to wind) -----------------
    const relN = state.vel.n - this.wind.north;
    const relE = state.vel.e - this.wind.east;
    const relD = state.vel.d - this.wind.down;
    // v_body = R^T * v_rel_world
    const u = R[0][0] * relN + R[1][0] * relE + R[2][0] * relD; // forward
    const v = R[0][1] * relN + R[1][1] * relE + R[2][1] * relD; // right
    const w = R[0][2] * relN + R[1][2] * relE + R[2][2] * relD; // down

    const V = Math.hypot(u, v, w); // true airspeed
    const Vsafe = Math.max(V, 1e-3);
    const alpha = Math.atan2(w, u);
    const beta = Math.asin(clamp(v / Vsafe, -1, 1));
    // CY_BETA/CL_BETA/CN_BETA are linear stability derivatives, valid only for
    // small sideslip; use a clamped value in those specific coefficients so a
    // parked aircraft in a beam wind (beta -> +-90 deg) can't generate an
    // unbounded roll/yaw moment. `beta` itself is left true for instruments.
    const betaAero = clamp(beta, -C.BETA_LIMIT, C.BETA_LIMIT);
    const qbar = 0.5 * rho * V * V; // dynamic pressure

    // --- Lift coefficient with flaps and a smooth stall -------------------
    const dCLflap = C.FLAP_DCL[controls.flapDetent];
    const dCLmaxFlap = C.FLAP_DCL_MAX[controls.flapDetent];
    const clMax = C.CL_MAX + dCLmaxFlap;
    const clLinear = C.CL0 + dCLflap + C.CL_ALPHA * alpha;
    const CL = stallBlend(clLinear, alpha, clMax);

    // --- Drag: parasite + induced + flap/gear -----------------------------
    // Gear drag only applies to aircraft profiles with retractable gear; the
    // C172S has HAS_RETRACTABLE_GEAR=false, so gearDown never changes drag.
    const gearDrag =
      C.HAS_RETRACTABLE_GEAR && controls.gearDown ? C.GEAR_DRAG_DELTA : 0;
    const CD =
      C.CD0 + C.FLAP_DCD[controls.flapDetent] + gearDrag + C.K_INDUCED * CL * CL;

    // --- Side force -------------------------------------------------------
    const CY = C.CY_BETA * betaAero;

    const S = C.WING_AREA;
    const b = C.WING_SPAN;
    const c = C.MEAN_CHORD;

    const lift = qbar * S * CL;
    const drag = qbar * S * CD;
    const sideForce = qbar * S * CY;

    // Aerodynamic forces in body frame (x fwd, y right, z down).
    // Lift is perpendicular to relative wind (mostly -z), drag opposes it.
    const ca = Math.cos(alpha), sa = Math.sin(alpha);
    let Fx = lift * sa - drag * ca;
    let Fz = -lift * ca - drag * sa;
    let Fy = sideForce;

    // Thrust (engine) along +x.
    const eng = this.engine.update(
      state.rpm,
      controls.throttle,
      controls.mixture,
      densityRatio(alt),
      V,
      dt,
    );
    state.rpm = eng.rpm;
    Fx += eng.thrust;
    state.fuel = Math.max(0, state.fuel - eng.fuelBurn * dt);
    if (state.fuel <= 0) this.engine.running = false;

    // Gravity in body frame: world down (0,0,+g) * mass, projected via R^T.
    const gWorld = C.G * C.MASS;
    Fx += R[2][0] * gWorld;
    Fy += R[2][1] * gWorld;
    Fz += R[2][2] * gWorld;

    // --- Aerodynamic moments (body) ---------------------------------------
    const de = controls.elevator * C.ELEVATOR_MAX;
    const da = controls.aileron * C.AILERON_MAX;
    const dr = controls.rudder * C.RUDDER_MAX;
    const trim = controls.trim * C.TRIM_MAX;

    // Non-dimensional rate terms
    const phat = (state.p * b) / (2 * Vsafe);
    const qhat = (state.q * c) / (2 * Vsafe);
    const rhat = (state.r * b) / (2 * Vsafe);

    const Cl =
      C.CL_BETA * betaAero + C.CL_P * phat + C.CL_DA * da + C.CN_DA * 0; // roll
    const Cm =
      C.CM0 +
      C.FLAP_PITCH[controls.flapDetent] +
      C.CM_ALPHA * alpha +
      C.CM_Q * qhat +
      C.CM_DE * de +
      C.CM_TRIM * trim;
    const Cn =
      C.CN_BETA * betaAero + C.CN_R * rhat + C.CN_DR * dr + C.CN_DA * da;

    let Lm = qbar * S * b * Cl;
    let Mm = qbar * S * c * Cm;
    let Nm = qbar * S * b * Cn;

    // --- Ground reaction: real 3-point tricycle gear -----------------------
    // Each wheel gets its own independent spring/damper + friction force at
    // its own body-frame position; roll/pitch/yaw ground behavior (righting
    // from a wing-low touchdown, nosewheel lifting off during rotation,
    // differential-braking yaw, directional damping from tire scrub) all
    // emerge from that geometry — no artificial "keep it level" moments.
    let onGround = false;
    for (const wheel of WHEELS) {
      // Per-wheel brake = whichever is stronger: the uniform "both wheels"
      // brake (keyboard toggle / digital pad button) or this side's
      // independent analog toe-brake axis — so binding pedals doesn't
      // disable the simple digital brake, and vice versa.
      const sideBrake = wheel.side === 'left' ? controls.brakeLeft : wheel.side === 'right' ? controls.brakeRight : 0;
      const brakeInput = Math.max(controls.brakes, sideBrake);
      // This wheel's horizontal (north, east) position, body -> world, so the
      // terrain height is sampled under the wheel itself, not the CG.
      const offN = R[0][0] * wheel.x + R[0][1] * wheel.y + R[0][2] * wheel.z;
      const offE = R[1][0] * wheel.x + R[1][1] * wheel.y + R[1][2] * wheel.z;
      const groundH = this.terrain.elevationAt(state.pos.n + offN, state.pos.e + offE);
      const wf = wheelGroundForce(
        wheel, R,
        state.vel.n, state.vel.e, state.vel.d,
        state.p, state.q, state.r,
        alt, groundH, controls.rudder, brakeInput, dt,
      );
      if (wf.touching) onGround = true;
      Fx += wf.Fx; Fy += wf.Fy; Fz += wf.Fz;
      Lm += wf.Lm; Mm += wf.Mm; Nm += wf.Nm;
    }
    state.onGround = onGround;

    // --- Integrate translational motion (semi-implicit Euler) -------------
    const ax = Fx / C.MASS, ay = Fy / C.MASS, az = Fz / C.MASS;
    // body accel -> world
    const aN = R[0][0] * ax + R[0][1] * ay + R[0][2] * az;
    const aE = R[1][0] * ax + R[1][1] * ay + R[1][2] * az;
    const aD = R[2][0] * ax + R[2][1] * ay + R[2][2] * az;

    state.vel.n += aN * dt;
    state.vel.e += aE * dt;
    state.vel.d += aD * dt;

    state.pos.n += state.vel.n * dt;
    state.pos.e += state.vel.e * dt;
    state.pos.d += state.vel.d * dt;

    // Hard floor so we never sink through the runway — now relative to the
    // ground surface under the CG (terrain MSL height), not a fixed MSL 0.
    const gndCG = this.terrain.elevationAt(state.pos.n, state.pos.e);
    const floorMSL = gndCG + C.GEAR_HEIGHT - 0.3;
    if (-state.pos.d < floorMSL) {
      state.pos.d = -floorMSL;
      if (state.vel.d > 0) state.vel.d = 0;
    }

    // --- Integrate rotational motion --------------------------------------
    const pdot = (Lm + (C.IY - C.IZ) * state.q * state.r) / C.IX;
    const qdot = (Mm + (C.IZ - C.IX) * state.p * state.r) / C.IY;
    const rdot = (Nm + (C.IX - C.IY) * state.p * state.q) / C.IZ;
    state.p += pdot * dt;
    state.q += qdot * dt;
    state.r += rdot * dt;

    // Euler angle kinematics from body rates.
    const tanth = Math.tan(clamp(state.theta, -1.45, 1.45));
    const phidot = state.p + (sphi * tanth) * state.q + (cphi * tanth) * state.r;
    const thetadot = cphi * state.q - sphi * state.r;
    const psidot = (sphi / cth) * state.q + (cphi / cth) * state.r;
    state.phi += phidot * dt;
    state.theta += thetadot * dt;
    state.psi += psidot * dt;
    state.psi = wrap2pi(state.psi);

    // --- Derived values for instruments -----------------------------------
    state.tas = V;
    state.alpha = alpha;
    state.beta = beta;
    state.altitude = alt;
    state.verticalSpeed = -state.vel.d;
    state.loadFactor = -Fz / gWorld + 0; // approx normal load factor
  }
}

/** Smoothly limit the lift curve near/after the stall. */
function stallBlend(clLinear: number, alpha: number, clMax: number): number {
  const stall = C.ALPHA_STALL;
  const a = Math.abs(alpha);
  if (a < stall) return clLinear;
  // Beyond the stall angle, blend from linear toward a decaying flat-plate value.
  const over = a - stall;
  const blend = Math.min(1, over / 0.20); // fully post-stall ~11.5 deg beyond
  const sign = clLinear >= 0 ? 1 : -1;
  const flatPlate = sign * clMax * 0.7 * Math.cos(over * 2.5);
  const capped = sign * Math.min(Math.abs(clLinear), clMax);
  return capped * (1 - blend) + flatPlate * blend;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function wrap2pi(a: number): number {
  const twoPi = Math.PI * 2;
  a %= twoPi;
  return a < 0 ? a + twoPi : a;
}
